'use client';
import Icon from '@/Icon';
import { stepinContentInstance, VideoInfo, VideoInfo_Tournament } from '@/legacy/apis/types/stepinContent';
import { clc } from '@/legacy/components/base/utils';
import AspectFillImage from '@/legacy/components/img/AspectFillImage';
import Img from '@/legacy/components/img/ImgFrame';
import styles from '@/legacy/components/modalContents/VideoModal.module.scss';
import { nullBannerReplace, nullCashReplace } from '@/utilities/stateTool';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';

type Props = {
	video: VideoInfo;
	url: string;
	active: boolean;
	tournament?: VideoInfo_Tournament;
};

const VideoRender = ({ video, url, active, tournament }: Props) => {
	console.log('🚀 ~ VideoRender ~ video, url, active:', video, url, active);
	const videoEl = useRef<HTMLVideoElement>(null);
	const ext = url && url.split('.').at(-1);
	const [load, setLoad] = useState(false);
	const [play, setPlay] = useState(false);
	const [show, setShow] = useState(false);
	const [aspect, setAspect] = useState(40 / 11);

	const timmer = useRef(setTimeout(() => {}));

	useEffect(() => {
		if (videoEl.current == null) return;
		if (active) {
			// 개발 할 때 자동 재생 시끄러워서 비활성화
			const promise = videoEl.current.play();
			promise
				.then(() => {
					if (videoEl.current) {
						// videoEl.current.muted =false
						// 개발할 때 소리 안나게
						videoEl.current.muted = process.env.NODE_ENV === 'development';
						const playPromise = videoEl.current.play();

						playPromise.catch((e) => {});
					}
				})
				.catch((error) => {
					console.log(error);
					if (videoEl.current) {
						videoEl.current.muted = true;
						const playPromise = videoEl.current.play();

						playPromise.catch((e) => {});
					}
				});
		} else {
			videoEl.current.pause();
		}
	}, [active, load]);

	useEffect(() => {
		const fn = async () => {
			const Hls = (await import('hls.js')).default;
			if (videoEl.current) {
				if (videoEl.current.paused === false) {
					videoEl.current.pause(); // 비디오가 재생 중이면 일시 정지
				}
			}
			if (ext && ext.toLowerCase() === 'm3u8') {
				if (videoEl.current) {
					if (Hls.isSupported()) {
						const hls = new Hls();
						hls.loadSource(url);
						hls.attachMedia(videoEl.current);
					} else if (videoEl.current.canPlayType('application/vnd.apple.mpegurl')) {
						videoEl.current.src = url;
						videoEl.current.load();
					}
				}
			} else {
				if (videoEl.current) {
					videoEl.current.src = url;
					videoEl.current.load();
				}
			}
		};
		fn();
	}, [videoEl.current]);

	const paused = videoEl.current && videoEl.current.paused;
	console.log(paused);

	const playToggle = () => {
		if (videoEl.current) {
			if (videoEl.current.paused) {
				const playPromise = videoEl.current.play();

				playPromise.catch((e) => {});
			} else {
				videoEl.current.pause();
			}
		}
	};
	const isTournament = tournament && tournament.bannerImageUrl;

	return (
		<div className={styles.flex}>
			<video
				className={clc(styles.video, load && styles.active)}
				controlsList="nofullscreen noremoteplayback"
				crossOrigin="anonymous"
				disablePictureInPicture
				loop
				muted
				onCanPlay={() => {
					setLoad(true);
				}}
				onClick={() => {
					playToggle();
				}}
				onMouseMove={() => {
					clearTimeout(timmer.current);
					setShow(true);
					timmer.current = setTimeout(() => {
						setShow(false);
					}, 1600);
				}}
				onPause={() => {
					setPlay(false);
				}}
				onPlay={(e) => {
					setPlay(true);
				}}
				playsInline
				poster={video.video.thumbnailUrl}
				ref={videoEl}
			>
				loading...
			</video>
			{load && (
				<button className={clc(styles.play, (show || !play) && styles.show)} onClick={playToggle}>
					{!play ? (
						<Icon className={styles.playIcon} color1={'#FDFDFE'} path="Action_Play_24_Black" />
					) : (
						<Icon className={styles.playIcon} color1={'#FDFDFE'} path="Action_Stop_24_Black" />
					)}
				</button>
			)}
			{isTournament &&
				(tournament.bannerUrl ? (
					<Link
						className={styles.banner}
						href={tournament.bannerUrl}
						style={{ aspectRatio: isNaN(aspect) ? 40 / 11 : aspect }}
						target="_blank"
					>
						<AspectFillImage
							onError={(e) => {
								e.currentTarget.src = nullBannerReplace(null);
								e.currentTarget.alt = 'placeholder image';
							}}
							setAspect={setAspect}
							src={nullBannerReplace(tournament?.bannerImageUrl)}
						/>
					</Link>
				) : (
					<div className={styles.banner} style={{ aspectRatio: isNaN(aspect) ? 40 / 11 : aspect }}>
						<AspectFillImage
							onError={(e) => {
								e.currentTarget.src = nullBannerReplace(null);
								e.currentTarget.alt = 'placeholder image';
							}}
							setAspect={setAspect}
							src={nullBannerReplace(tournament?.bannerImageUrl)}
							style={{ aspectRatio: isNaN(aspect) ? 40 / 11 : aspect }}
						/>
					</div>
				))}
		</div>
	);
};

type ProviderProps = {
	id: string;
	thumbnailUrl: string;
	tournament?: VideoInfo_Tournament;
};

const { POST } = stepinContentInstance;

const getTournamentChampion = async (videoIdList: string[]) => {
	const { data, error } = await POST('/video/list/info', {
		body: {
			videoIdList,
		},
	});

	if (data) return data.data;
	if (error) throw error;
};

const VideoModal = ({ id, thumbnailUrl, tournament }: ProviderProps) => {
	const { data, error, isLoading } = useSWR(['videoModal', id], ([_, id]) => getTournamentChampion([id]));
	console.log('🚀 ~ VideoModal ~ data:', id, data);

	if (data && data.videoList[0]) {
		// @ts-ignore
		const video = data.videoList[0].video as VideoInfo;
		const url = video.videoUrl;

		const active = true;

		return <VideoRender active={active} tournament={tournament} url={url} video={data.videoList[0]} />;
	}

	if (isLoading) return <Img className={styles.flex} src={nullCashReplace(thumbnailUrl)} />;
	if (error) return <div>{JSON.stringify(error)}</div>;
	return null;
};

export default VideoModal;
